import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Wallet, Sparkles, Info } from "lucide-react";
import PlatformTermsDialog from "./PlatformTermsDialog";
import {
    SiMeta,
    SiGoogle,
    SiTiktok,
    SiSnapchat,
} from "react-icons/si";
import { useLanguage } from "../../contexts/LanguageContext";
import { useSiteSettings } from "../../contexts/SiteSettingsContext";

const BingMark = () => (
    <span className="font-display font-black text-2xl tracking-tight">b</span>
);
const TaboolaMark = () => (
    <span className="font-display font-black text-2xl tracking-tight">T</span>
);
const OutbrainMark = () => (
    <span className="font-display font-black text-2xl tracking-tight">O</span>
);

const MIN_TOPUP_KEY = {
    meta: "min_topup_meta_standard",
    google: "min_topup_google",
    tiktok: "min_topup_tiktok",
    snapchat: "min_topup_snapchat",
    bing: "min_topup_bing",
    taboola: "min_topup_taboola",
    outbrain: "min_topup_outbrain",
};

// bullet text → { tr, es, pt, ru } (English is the key itself)
const BULLET_I18N = {
    "Compliance-first account operations": { tr: "Uyum odaklı hesap operasyonu", es: "Operación de cuentas con enfoque en cumplimiento", pt: "Operação de contas com foco em conformidade", ru: "Ведение аккаунтов с приоритетом комплаенса" },
    "Broad performance vertical coverage": { tr: "Geniş performans dikeyi kapsamı", es: "Amplia cobertura de verticales performance", pt: "Ampla cobertura de verticais performance", ru: "Широкий охват performance-вертикалей" },
    "High-volume media buying workflows": { tr: "Yüksek hacimli medya satın alma akışı", es: "Flujos de media buying de alto volumen", pt: "Fluxos de media buying de alto volume", ru: "Процессы медиабаинга больших объёмов" },
    "Whitelisted MCC seats": { tr: "Whitelisted MCC koltukları", es: "Asientos MCC whitelisted", pt: "Assentos MCC whitelisted", ru: "Whitelisted MCC-места" },
    "Faster ad approvals": { tr: "Daha hızlı reklam onayı", es: "Aprobaciones de anuncios más rápidas", pt: "Aprovações de anúncios mais rápidas", ru: "Быстрее одобрение рекламы" },
    "Multi-GEO ad accounts": { tr: "Çoklu GEO reklam hesapları", es: "Cuentas publicitarias multi-GEO", pt: "Contas de anúncios multi-GEO", ru: "Мульти-GEO рекламные аккаунты" },
    "High scaling capacity": { tr: "Yüksek ölçekleme kapasitesi", es: "Alta capacidad de escalado", pt: "Alta capacidade de escala", ru: "Высокая ёмкость масштабирования" },
    "Spark Ads support": { tr: "Spark Ads desteği", es: "Soporte para Spark Ads", pt: "Suporte a Spark Ads", ru: "Поддержка Spark Ads" },
    "Creative review < 1h": { tr: "Kreatif inceleme < 1 saat", es: "Revisión de creativos < 1 h", pt: "Revisão de criativos < 1 h", ru: "Проверка креативов < 1 ч" },
    "Gen Z attention at scale": { tr: "Ölçekli Gen Z erişimi", es: "Atención Gen Z a escala", pt: "Atenção da Gen Z em escala", ru: "Внимание поколения Z в масштабе" },
    "AR Lens campaigns": { tr: "AR Lens kampanyaları", es: "Campañas AR Lens", pt: "Campanhas AR Lens", ru: "AR Lens-кампании" },
    "Multi-GEO targeting": { tr: "Çoklu GEO hedefleme", es: "Segmentación multi-GEO", pt: "Segmentação multi-GEO", ru: "Мульти-GEO таргетинг" },
    "Tier-1 desktop audience": { tr: "Tier-1 masaüstü kitle", es: "Audiencia desktop tier-1", pt: "Público desktop tier-1", ru: "Десктопная аудитория tier-1" },
    "30-50% lower CPC": { tr: "%30-50 daha düşük CPC", es: "CPC 30-50% más bajo", pt: "CPC 30-50% mais baixo", ru: "CPC на 30-50% ниже" },
    "Direct rep escalation": { tr: "Doğrudan temsilci eskalasyonu", es: "Escalado directo con representante", pt: "Escalonamento direto com representante", ru: "Прямая эскалация через менеджера" },
    "Advertorial-friendly": { tr: "Advertorial dostu", es: "Apto para advertoriales", pt: "Amigável a advertoriais", ru: "Подходит для адверториалов" },
    "USA Today · Bloomberg · MSN": { tr: "USA Today · Bloomberg · MSN", es: "USA Today · Bloomberg · MSN", pt: "USA Today · Bloomberg · MSN", ru: "USA Today · Bloomberg · MSN" },
    "Long-form LP support": { tr: "Uzun form LP desteği", es: "Soporte para landings largas", pt: "Suporte a landings longas", ru: "Поддержка длинных лендингов" },
    "Premium news inventory": { tr: "Premium haber envanteri", es: "Inventario de noticias premium", pt: "Inventário de notícias premium", ru: "Премиальный новостной инвентарь" },
    "CNN · WaPo · The Guardian": { tr: "CNN · WaPo · The Guardian", es: "CNN · WaPo · The Guardian", pt: "CNN · WaPo · The Guardian", ru: "CNN · WaPo · The Guardian" },
    "Compliance pre-review": { tr: "Uyum ön incelemesi", es: "Pre-revisión de cumplimiento", pt: "Pré-revisão de conformidade", ru: "Предварительная проверка на соответствие" },
};

const FAMILY_I18N = {
    "Native discovery · premium publishers": { tr: "Native discovery · premium yayıncılar", es: "Native discovery · publishers premium", pt: "Native discovery · publishers premium", ru: "Native discovery · премиальные площадки" },
    "Native discovery · top news placements": { tr: "Native discovery · en iyi haber yerleşimleri", es: "Native discovery · principales medios de noticias", pt: "Native discovery · principais portais de notícias", ru: "Native discovery · топовые новостные размещения" },
};

const TAG_I18N = {
    "Most popular": { es: "Más popular", pt: "Mais popular", ru: "Самый популярный" },
    "High intent": { es: "Alta intención", pt: "Alta intenção", ru: "Высокий интент" },
    "Fastest growth": { es: "Mayor crecimiento", pt: "Crescimento mais rápido", ru: "Самый быстрый рост" },
    "Lower CPM": { es: "CPM más bajo", pt: "CPM mais baixo", ru: "Ниже CPM" },
    "Lower CPC": { es: "CPC más bajo", pt: "CPC mais baixo", ru: "Ниже CPC" },
    "Native": { es: "Native", pt: "Native", ru: "Native" },
};

const platforms = [
    {
        slug: "meta",
        name: "Meta",
        Icon: SiMeta,
        family: "Facebook · Instagram · Messenger · WhatsApp",
        accent: "#1877F2",
        tag: { tr: "En popüler", en: "Most popular" },
        bullets: [
            "Compliance-first account operations",
            "Broad performance vertical coverage",
            "High-volume media buying workflows",
        ],
        feature: { tr: "Hacim bazlı fee %4'ten başlar", en: "Volumetric fees start at 4%" },
    },
    {
        slug: "google",
        name: "Google",
        Icon: SiGoogle,
        family: "Search · YouTube · Display · Shopping · PMax",
        accent: "#fff",
        tag: { tr: "Yüksek niyet", en: "High intent" },
        bullets: [
            "Whitelisted MCC seats",
            "Faster ad approvals",
            "Multi-GEO ad accounts",
        ],
        feature: { tr: "Hacim bazlı fee %4'ten başlar", en: "Volumetric fees start at 4%" },
    },
    {
        slug: "tiktok",
        name: "TikTok",
        Icon: SiTiktok,
        family: "Spark Ads · In-Feed · TopView · Pulse",
        accent: "#fff",
        tag: { tr: "En hızlı büyüme", en: "Fastest growth" },
        bullets: [
            "High scaling capacity",
            "Spark Ads support",
            "Creative review < 1h",
        ],
        feature: { tr: "Hacim bazlı fee %4'ten başlar", en: "Volumetric fees start at 4%" },
    },
    {
        slug: "snapchat",
        name: "Snapchat",
        Icon: SiSnapchat,
        family: "Snap Ads · AR Lenses · Story Ads · Spotlight",
        accent: "#FFFC00",
        tag: { tr: "Düşük CPM", en: "Lower CPM" },
        bullets: [
            "Gen Z attention at scale",
            "AR Lens campaigns",
            "Multi-GEO targeting",
        ],
        feature: { tr: "Hacim bazlı fee %4'ten başlar", en: "Volumetric fees start at 4%" },
    },
    {
        slug: "bing",
        name: "Bing / Microsoft",
        Icon: BingMark,
        family: "Search · Audience Network · Outlook · MSN",
        accent: "#fff",
        tag: { tr: "Daha düşük CPC", en: "Lower CPC" },
        bullets: [
            "Tier-1 desktop audience",
            "30-50% lower CPC",
            "Direct rep escalation",
        ],
        feature: { tr: "Hacim bazlı fee %4'ten başlar", en: "Volumetric fees start at 4%" },
    },
    {
        slug: "taboola",
        name: "Taboola",
        Icon: TaboolaMark,
        family: "Native discovery · premium publishers",
        accent: "#fff",
        tag: { tr: "Native", en: "Native" },
        bullets: [
            "Advertorial-friendly",
            "USA Today · Bloomberg · MSN",
            "Long-form LP support",
        ],
        feature: { tr: "Hacim bazlı fee %4'ten başlar", en: "Volumetric fees start at 4%" },
    },
    {
        slug: "outbrain",
        name: "Outbrain",
        Icon: OutbrainMark,
        family: "Native discovery · top news placements",
        accent: "#fff",
        tag: { tr: "Native", en: "Native" },
        bullets: [
            "Premium news inventory",
            "CNN · WaPo · The Guardian",
            "Compliance pre-review",
        ],
        feature: { tr: "Hacim bazlı fee %4'ten başlar", en: "Volumetric fees start at 4%" },
    },
];

export default function PlatformsSection() {
    const { language } = useLanguage();

    const copy = {
        allIncluded: { en: "All included", tr: "Hepsi dahil", es: "Todo incluido", pt: "Tudo incluído", ru: "Всё включено" },
        allTitle: { en: "7 platforms, one account, $0 monthly.", tr: "7 platform, tek hesap, $0 aylık.", es: "7 plataformas, una cuenta, $0 al mes.", pt: "7 plataformas, uma conta, $0 por mês.", ru: "7 платформ, один аккаунт, $0 в месяц." },
        allSub: { en: "No subscription. Fund your wallet, request an account on any platform, and pay a fee only on what you top up.", tr: "Abonelik yok. Cüzdanına yükle, istediğin platformda hesap talep et; sadece yüklediğin tutar üzerinden komisyon öde.", es: "Sin suscripción. Fondea tu wallet, solicita una cuenta en cualquier plataforma y paga comisión solo sobre lo que recargas.", pt: "Sem assinatura. Abasteça sua wallet, solicite uma conta em qualquer plataforma e pague taxa apenas sobre o que recarrega.", ru: "Без подписки. Пополните кошелёк, запросите аккаунт на любой платформе и платите комиссию только с суммы пополнения." },
        minVaries: { en: "Min. top-up varies by platform", tr: "Min. yükleme platforma göre", es: "La recarga mín. varía según la plataforma", pt: "A recarga mín. varia por plataforma", ru: "Мин. пополнение зависит от платформы" },
        freeSignup: { en: "Create free account", tr: "Ücretsiz kayıt", es: "Crear cuenta gratis", pt: "Criar conta grátis", ru: "Создать бесплатный аккаунт" },
        termsBtn: { en: "Top-up terms & commission", tr: "Yükleme koşulları & komisyon", es: "Condiciones de recarga y comisión", pt: "Condições de recarga e comissão", ru: "Условия пополнения и комиссия" },
        noFee: { en: "No account fee", tr: "Hesap ücreti yok", es: "Sin cuota de cuenta", pt: "Sem taxa de conta", ru: "Без платы за аккаунт" },
        titleLead: {
            en: "Seven channels.",
            pt: "Sete canais.",
            es: "Siete canales.",
            ru: "Семь каналов.",
            tr: "Yedi kanal.",
        },
        titleTail: {
            en: "One operator.",
            pt: "Um só operador.",
            es: "Un solo operador.",
            ru: "Один оператор.",
            tr: "Tek operatör.",
        },
        subtitle: {
            en: "Whitelisted Platinum HIVA Credit Line accounts across all tier-1 traffic sources. No monthly subscription — every platform included, you only pay a volumetric top-up fee from 4%.",
            pt: "Contas whitelisted Platinum HIVA com Credit Line em todas as fontes tier-1. Sem assinatura mensal — todas as plataformas incluídas, apenas taxa de recarga volumétrica a partir de 4%.",
            es: "Cuentas whitelisted Platinum HIVA con Credit Line en todas las fuentes tier-1. Sin suscripción mensual — todas las plataformas incluidas, solo comisión de recarga volumétrica desde 4%.",
            ru: "Whitelisted Platinum HIVA Credit Line аккаунты во всех tier-1 источниках. Без ежемесячной подписки — все платформы включены, только объемная комиссия за пополнение от 4%.",
            tr: "Tier-1 trafik kaynaklarının tamamında whitelisted Platinum HIVA Credit Line hesapları. Aylık abonelik yok — tüm platformlar dahil, sadece %4'ten başlayan hacimsel yükleme komisyonu.",
        },
        detail: {
            en: "Explore",
            pt: "Explorar",
            es: "Detalle",
            ru: "Подробнее",
            tr: "Detay",
        },
        perMonth: {
            en: "/mo · no subscription",
            pt: "/mês · sem assinatura",
            es: "/mes · sin suscripción",
            ru: "/мес · без подписки",
            tr: "/ay · abonelik yok",
        },
    };

    const text = (k) => copy[k]?.[language] || copy[k]?.en || "";
    const { settings } = useSiteSettings();
    const minTopupFor = (slug) => Number(settings?.[MIN_TOPUP_KEY[slug]] || 250);
    const [termsFor, setTermsFor] = useState(null);

    return (
        <section
            id="platforms"
            data-testid="platforms-section"
            className="py-24 md:py-32"
        >
            <PlatformTermsDialog platform={termsFor} minTopup={termsFor ? minTopupFor(termsFor.slug) : 0} minTopupFlex={Number(settings?.min_topup_meta_flex || 500)} language={language} onClose={() => setTermsFor(null)} />
            <div className="container-skala">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
                    <div className="max-w-2xl space-y-4">
                        <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-highlight">
                            01 — {({ tr: "Platformlar", es: "Plataformas", pt: "Plataformas", ru: "Платформы" })[language] || "Platforms"}
                        </p>
                        <h2 className="font-display font-semibold text-4xl md:text-5xl tracking-tight">
                            {text("titleLead")} {" "}
                            <span className="serif-accent text-foreground/85">
                                {text("titleTail")}
                            </span>
                        </h2>
                    </div>
                    <p className="text-muted-foreground max-w-md">
                        {text("subtitle")}
                    </p>
                </div>

                <div className="border-t border-white/[0.08]">
                    {platforms.map((p, idx) => (
                        <Link
                            key={p.slug}
                            to={`/platforms/${p.slug}`}
                            data-testid={`platform-card-${p.slug}`}
                            className="group grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-4 items-center py-7 md:py-8 border-b border-white/[0.07] transition-colors hover:bg-white/[0.02] cursor-pointer"
                        >
                            {/* Identity */}
                            <div className="md:col-span-4 flex items-center gap-4 min-w-0">
                                <span className="font-display text-sm text-highlight/50 tabular-nums w-6 shrink-0">{String(idx + 1).padStart(2, "0")}</span>
                                <span className="w-12 h-12 rounded-xl bg-white/[0.04] border border-white/10 grid place-items-center shrink-0 group-hover:border-highlight/40 group-hover:bg-white/[0.06] transition-colors" style={{ color: p.accent }}>
                                    <p.Icon className="w-6 h-6" />
                                </span>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-display text-2xl tracking-tight truncate">{p.name}</h3>
                                        <span className="hidden xl:inline text-[9px] font-mono uppercase tracking-[0.18em] text-highlight/80 border border-highlight/20 bg-highlight/[0.06] rounded-full px-2 py-0.5 shrink-0">{p.tag[language] || TAG_I18N[p.tag.en]?.[language] || p.tag.en}</span>
                                    </div>
                                    <p className="text-[11px] font-mono text-muted-foreground tracking-wide mt-1 truncate">{FAMILY_I18N[p.family]?.[language] || p.family}</p>
                                </div>
                            </div>

                            {/* Feature pills */}
                            <div className="md:col-span-5 pl-9 md:pl-0 min-w-0">
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
                                    <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.14em] text-highlight border border-highlight/20 bg-highlight/[0.06] rounded-full px-2.5 py-1 whitespace-nowrap">
                                        <span className="w-1.5 h-1.5 rounded-full bg-highlight" />{text("noFee")}
                                    </span>
                                    {p.bullets.map((b) => (
                                        <span key={b} className="text-[11px] text-foreground/60 border border-white/[0.08] rounded-full px-2.5 py-1 whitespace-nowrap group-hover:border-white/[0.14] transition-colors">
                                            {BULLET_I18N[b]?.[language] || b}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Action */}
                            <div className="md:col-span-3 pl-9 md:pl-0 flex items-center justify-start md:justify-end gap-3 min-w-0">
                                <button
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTermsFor(p); }}
                                    data-testid={`platform-terms-btn-${p.slug}`}
                                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-white/10 hover:border-highlight/40 hover:bg-white/[0.04] text-xs text-foreground/70 hover:text-foreground transition-colors whitespace-nowrap"
                                >
                                    <Info className="w-3.5 h-3.5" /> {text("termsBtn")}
                                </button>
                                <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-highlight group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0" />
                            </div>
                        </Link>
                    ))}

                    {/* Final row: all platforms included */}
                    <Link
                        to="/signup"
                        data-testid="platform-card-all-included"
                        className="group grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-5 items-center py-8 md:py-9 border-b border-white/[0.07] bg-highlight/[0.03] hover:bg-highlight/[0.06] transition-colors cursor-pointer"
                    >
                        <div className="md:col-span-7 flex items-center gap-4 min-w-0">
                            <span className="w-12 h-12 rounded-xl bg-highlight/12 border border-highlight/30 grid place-items-center shrink-0">
                                <Sparkles className="w-5 h-5 text-highlight" />
                            </span>
                            <div className="min-w-0">
                                <h3 className="font-display text-2xl tracking-tight">{text("allTitle")}</h3>
                                <p className="text-sm text-muted-foreground mt-1 max-w-lg">{text("allSub")}</p>
                            </div>
                        </div>
                        <div className="md:col-span-5 pl-16 md:pl-0 flex items-center justify-start md:justify-end gap-5 min-w-0">
                            <span className="inline-flex items-center gap-2 text-xs font-mono text-muted-foreground whitespace-nowrap">
                                <Wallet className="w-3.5 h-3.5" /> {text("minVaries")}
                            </span>
                            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-highlight whitespace-nowrap">
                                {text("freeSignup")}
                                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </span>
                        </div>
                    </Link>
                </div>
            </div>
        </section>
    );
}
